import { cloneElement, getElementObjectUrl } from './documentSchema';

const HISTORY_LIMIT = 40;

const collectObjectUrls = (elements = []) => elements.map((element) => getElementObjectUrl(element)).filter(Boolean);

const collectObjectUrlsFromSnapshots = (snapshots = []) => {
  const urls = new Set();
  snapshots.forEach((snapshot) => {
    collectObjectUrls(snapshot.elements).forEach((url) => urls.add(url));
  });
  return urls;
};

const revokeUrl = (url) => {
  if (url) {
    URL.revokeObjectURL(url);
  }
};

const closeImage = (image) => {
  if (image?.close) {
    image.close();
  }
};

export const cloneDocumentSnapshot = (state) => ({
  width: state.width,
  height: state.height,
  background: { ...state.background },
  outputSize: state.outputSize ? { ...state.outputSize } : null,
  elements: (state.elements || []).map(cloneElement),
  selectedId: state.selectedId,
  selectedIds: [...(state.selectedIds || [])],
});

export const createEditorHistory = (limit = HISTORY_LIMIT) => {
  let past = [];
  let future = [];

  const getReferencedUrls = (currentElements) => {
    const urls = new Set(collectObjectUrls(currentElements));
    collectObjectUrlsFromSnapshots(past).forEach((url) => urls.add(url));
    collectObjectUrlsFromSnapshots(future).forEach((url) => urls.add(url));
    return urls;
  };

  const pruneUnreferenced = (currentElements, discardedElements = []) => {
    const referenced = getReferencedUrls(currentElements);
    const seen = new Set();

    discardedElements.forEach((element) => {
      const url = getElementObjectUrl(element);
      if (!url || referenced.has(url) || seen.has(url)) {
        return;
      }
      seen.add(url);
      revokeUrl(url);
      closeImage(element.image);
    });
  };

  const clear = (currentElements = []) => {
    const discarded = [...past.flatMap((s) => s.elements), ...future.flatMap((s) => s.elements)];
    past = [];
    future = [];
    pruneUnreferenced(currentElements, discarded);
  };

  const push = (snapshot) => {
    past = [...past, cloneDocumentSnapshot(snapshot)];
    if (past.length > limit) {
      const removed = past.shift();
      pruneUnreferenced(snapshot.elements, removed?.elements || []);
    }
    const discardedFuture = future.flatMap((s) => s.elements);
    future = [];
    pruneUnreferenced(snapshot.elements, discardedFuture);
  };

  const undo = (currentSnapshot) => {
    if (!past.length) {
      return null;
    }
    const previous = past[past.length - 1];
    past = past.slice(0, -1);
    future = [...future, cloneDocumentSnapshot(currentSnapshot)];
    return previous;
  };

  const redo = (currentSnapshot) => {
    if (!future.length) {
      return null;
    }
    const next = future[future.length - 1];
    future = future.slice(0, -1);
    past = [...past, cloneDocumentSnapshot(currentSnapshot)];
    return next;
  };

  return {
    push,
    undo,
    redo,
    clear,
    pruneUnreferenced,
    canUndo: () => past.length > 0,
    canRedo: () => future.length > 0,
  };
};
